import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { User, Save, Loader2, Mail, Calendar, Target, Dumbbell, Crown, Zap, Scale, Ruler, Trophy, Flame, Award, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import Paywall from "@/components/Paywall";

export default function Profile() {
  const { user, authHeaders, subscription } = useAuth();
  const [loading, setLoading] = useState(false);
  const [gamification, setGamification] = useState(null);
  const [form, setForm] = useState({
    name: "",
    age: "",
    weight: "",
    height: "",
    goal: "maintaining",
    experience_level: "beginner"
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        age: user.profile?.age || "",
        weight: user.profile?.weight || "",
        height: user.profile?.height || "",
        goal: user.profile?.goal || "maintaining",
        experience_level: user.profile?.experience_level || "beginner"
      });
    }
    fetchGamification();
  }, [user]);

  const fetchGamification = async () => {
    try {
      const res = await axios.get(`${API}/gamification/stats`, { headers: authHeaders() });
      setGamification(res.data);
    } catch (err) {
      console.error("Error fetching gamification:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        name: form.name || null,
        age: form.age ? parseInt(form.age) : null,
        weight: form.weight ? parseFloat(form.weight) : null,
        height: form.height ? parseFloat(form.height) : null,
        goal: form.goal,
        experience_level: form.experience_level
      };
      await axios.put(`${API}/profile`, data, { headers: authHeaders() });
      toast.success("Profil opdateret!");
    } catch (err) {
      toast.error("Fejl ved opdatering");
    } finally {
      setLoading(false);
    }
  };

  const goalLabels = {
    bulking: "Bulking (opbygning)",
    cutting: "Cutting (vægttab)",
    maintaining: "Vedligeholdelse",
    strength: "Styrke",
    endurance: "Udholdenhed"
  };

  const goalIcons = {
    bulking: "💪",
    cutting: "🔥",
    maintaining: "⚖️",
    strength: "🏋️",
    endurance: "🏃"
  };

  const levelLabels = {
    beginner: "Begynder",
    intermediate: "Middel",
    advanced: "Avanceret"
  };

  const levelIcons = {
    beginner: "🌱",
    intermediate: "🌿",
    advanced: "🌳"
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="profile-page">
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Left Sidebar - Profile Card */}
            <div className="lg:col-span-1 space-y-4">
              
              {/* Avatar & Basic Info */}
              <div className="bg-gradient-to-br from-[#18181b] to-[#0f0f12] border border-white/5 rounded-xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-[#DFFF00]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                
                <div className="relative text-center">
                  {/* Avatar */}
                  <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#DFFF00]/20 to-[#DFFF00]/5 flex items-center justify-center border-2 border-[#DFFF00]/20">
                    <span style={{ fontFamily: 'Oswald' }} className="text-3xl font-bold text-[#DFFF00]">
                      {getInitials(user?.name)}
                    </span>
                  </div>
                  
                  {/* Name */}
                  <h2 style={{ fontFamily: 'Oswald' }} className="text-2xl font-bold text-white uppercase tracking-tight" data-testid="profile-display-name">
                    {user?.name || "Ingen navn"}
                  </h2>
                  <p className="text-sm text-zinc-500 mt-1" data-testid="profile-email">{user?.email}</p>
                  
                  {/* Subscription Badge */}
                  {subscription && (
                    <div className="mt-4">
                      {subscription.subscription_active ? (
                        <div className="inline-flex items-center gap-2 bg-[#DFFF00]/10 border border-[#DFFF00]/20 rounded-full px-4 py-2" data-testid="profile-subscription-badge">
                          <Crown className="h-4 w-4 text-[#DFFF00]" />
                          <span className="text-sm font-bold text-[#DFFF00]">PRO MEDLEM</span>
                        </div>
                      ) : subscription.trial_active ? (
                        <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2" data-testid="profile-trial-badge">
                          <Zap className="h-4 w-4 text-orange-400" />
                          <span className="text-sm font-bold text-orange-400">PRØVEPERIODE ({subscription.trial_days_left} DAGE)</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-2">
                          <span className="text-sm font-bold text-red-400">UDLØBET</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Member Since */}
                <div className="mt-6 pt-6 border-t border-white/5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-500 flex items-center gap-2">
                      <Calendar className="h-4 w-4" /> Medlem siden
                    </span>
                    <span className="text-white" data-testid="profile-created">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString("da-DK", { month: "short", year: "numeric" }) : "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Level & XP Card */}
              {gamification && (
                <div className="bg-gradient-to-br from-purple-500/10 via-[#18181b] to-[#18181b] border border-white/5 rounded-xl p-6" data-testid="profile-level-card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-2xl">
                      {gamification.level?.level_icon || "🌱"}
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Dit Level</p>
                      <p className="text-lg font-bold text-white">{gamification.level?.level_name || "Begynder"}</p>
                    </div>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs text-zinc-500">XP</span>
                      <span style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-purple-400">{gamification.total_xp || 0}</span>
                    </div>
                    <Progress value={gamification.level?.progress_percent || 0} className="h-2 bg-zinc-800" />
                    {gamification.level?.next_level_xp && (
                      <p className="text-xs text-zinc-500 mt-2">
                        {gamification.level.next_level_xp - gamification.total_xp} XP til {gamification.level.next_level_name}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-zinc-900/30 rounded-lg p-3 text-center">
                      <Flame className="h-4 w-4 text-orange-400 mx-auto mb-1" />
                      <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-white">{gamification.current_streak || 0}</p>
                      <p className="text-[10px] text-zinc-500 uppercase">Streak</p>
                    </div>
                    <div className="bg-zinc-900/30 rounded-lg p-3 text-center">
                      <Award className="h-4 w-4 text-yellow-400 mx-auto mb-1" />
                      <p style={{ fontFamily: 'JetBrains Mono' }} className="text-lg font-bold text-white">{gamification.achievements_count || 0}</p>
                      <p className="text-[10px] text-zinc-500 uppercase">Badges</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Edit Form */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Header */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#DFFF00]/10 flex items-center justify-center">
                  <Settings className="h-6 w-6 text-[#DFFF00]" />
                </div>
                <div>
                  <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl sm:text-3xl uppercase tracking-tight text-white">
                    Profil Indstillinger
                  </h1>
                  <p className="text-sm text-zinc-500">Opdater dine personlige oplysninger</p>
                </div>
              </div>

              {/* Personal Info Form */}
              <div className="bg-[#18181b] border border-white/5 rounded-xl overflow-hidden" data-testid="edit-profile-card">
                <div className="p-4 border-b border-white/5 flex items-center gap-2">
                  <User className="h-5 w-5 text-[#DFFF00]" />
                  <span style={{ fontFamily: 'Oswald' }} className="uppercase text-sm tracking-wider text-white">Personlig Information</span>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">Navn</Label>
                    <Input 
                      value={form.name} 
                      onChange={e => setForm({...form, name: e.target.value})}
                      className="bg-zinc-900 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12 mt-2 text-white" 
                      placeholder="Dit fulde navn"
                      data-testid="profile-name-input" 
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5" /> Alder
                      </Label>
                      <Input 
                        type="number" 
                        value={form.age} 
                        onChange={e => setForm({...form, age: e.target.value})}
                        className="bg-zinc-900 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12 mt-2" 
                        placeholder="25"
                        data-testid="profile-age-input" 
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                        <Scale className="h-3.5 w-3.5" /> Vægt (kg)
                      </Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={form.weight} 
                        onChange={e => setForm({...form, weight: e.target.value})}
                        className="bg-zinc-900 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12 mt-2" 
                        placeholder="75.0"
                        data-testid="profile-weight-input" 
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                        <Ruler className="h-3.5 w-3.5" /> Højde (cm)
                      </Label>
                      <Input 
                        type="number" 
                        step="0.1" 
                        value={form.height} 
                        onChange={e => setForm({...form, height: e.target.value})}
                        className="bg-zinc-900 border-zinc-800 focus:border-[#DFFF00] rounded-xl h-12 mt-2" 
                        placeholder="180"
                        data-testid="profile-height-input" 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                        <Target className="h-3.5 w-3.5" /> Træningsmål
                      </Label>
                      <Select value={form.goal} onValueChange={v => setForm({...form, goal: v})}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-800 rounded-xl h-12 mt-2" data-testid="profile-goal-select">
                          <SelectValue>
                            <span className="flex items-center gap-2">
                              <span>{goalIcons[form.goal]}</span>
                              <span>{goalLabels[form.goal]}</span>
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {Object.entries(goalLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              <span className="flex items-center gap-2">
                                <span>{goalIcons[value]}</span>
                                <span>{label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                        <Dumbbell className="h-3.5 w-3.5" /> Erfaringsniveau
                      </Label>
                      <Select value={form.experience_level} onValueChange={v => setForm({...form, experience_level: v})}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-800 rounded-xl h-12 mt-2" data-testid="profile-level-select">
                          <SelectValue>
                            <span className="flex items-center gap-2">
                              <span>{levelIcons[form.experience_level]}</span>
                              <span>{levelLabels[form.experience_level]}</span>
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {Object.entries(levelLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              <span className="flex items-center gap-2">
                                <span>{levelIcons[value]}</span>
                                <span>{label}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={loading} 
                    data-testid="save-profile-btn"
                    className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] rounded-xl font-bold uppercase tracking-wider h-12"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Gemmer...</>
                    ) : (
                      <><Save className="mr-2 h-5 w-5" /> Gem Ændringer</>
                    )}
                  </Button>
                </form>
              </div>

              {/* Quick Stats */}
              {(form.weight || form.height) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {form.weight && (
                    <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-white/5 rounded-xl p-4">
                      <Scale className="h-5 w-5 text-blue-400 mb-2" />
                      <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-white">{form.weight} kg</p>
                      <p className="text-xs text-zinc-500">Nuværende vægt</p>
                    </div>
                  )}
                  {form.height && (
                    <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-white/5 rounded-xl p-4">
                      <Ruler className="h-5 w-5 text-emerald-400 mb-2" />
                      <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-white">{form.height} cm</p>
                      <p className="text-xs text-zinc-500">Højde</p>
                    </div>
                  )}
                  {form.weight && form.height && (
                    <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-white/5 rounded-xl p-4">
                      <Target className="h-5 w-5 text-purple-400 mb-2" />
                      <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-white">
                        {(parseFloat(form.weight) / Math.pow(parseFloat(form.height) / 100, 2)).toFixed(1)}
                      </p>
                      <p className="text-xs text-zinc-500">BMI</p>
                    </div>
                  )}
                  {form.age && (
                    <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-white/5 rounded-xl p-4">
                      <Calendar className="h-5 w-5 text-orange-400 mb-2" />
                      <p style={{ fontFamily: 'JetBrains Mono' }} className="text-xl font-bold text-white">{form.age} år</p>
                      <p className="text-xs text-zinc-500">Alder</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Paywall>
  );
}
